import { useState, useEffect } from 'react';
import { FiUser, FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { formatRoleName } from '../utils/modulePermissions';

/**
 * Selector de rol para el módulo de Historicidad
 * Permite a usuarios con múltiples roles funcionales elegir con cuál operar
 */
const HistoricidadRoleSelector = ({ onRoleChange, selectedRole }) => {
  const { user } = useAuth();
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Cargar roles disponibles al montar el componente
  useEffect(() => {
    if (user && user.id) {
      loadAvailableRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Restaurar rol seleccionado desde localStorage al montar
  useEffect(() => {
    if (user && user.id && !selectedRole && availableRoles.length > 0) {
      const savedRole = localStorage.getItem(`historicidad_selected_role_${user.id}`);
      if (savedRole && availableRoles.some(r => r.value === savedRole)) {
        console.log('HistoricidadRoleSelector: Restoring saved role', savedRole);
        onRoleChange(savedRole);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, availableRoles]);

  const loadAvailableRoles = async () => {
    if (!user || !user.id) {
      console.log('HistoricidadRoleSelector: No user or user.id');
      return;
    }

    try {
      console.log('HistoricidadRoleSelector: Loading roles for user', user.id);
      
      // Obtener permisos del usuario en el módulo de historicidad
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/users/${user.id}/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.error('HistoricidadRoleSelector: Response not OK', response.status);
        throw new Error('Error al cargar permisos');
      }

      const data = await response.json();
      console.log('HistoricidadRoleSelector: Permissions data', data);
      
      const permissions = Array.isArray(data.data) ? data.data : [];

      // Filtrar solo permisos del módulo historicidad que estén activos
      const historicidadRoles = permissions
        .filter(p => p.moduleName === 'historicidad' && p.isActive)
        .map(p => ({
          value: p.roleName,
          label: formatRoleName(p.roleName),
          description: getRoleDescription(p.roleName)
        }));

      console.log('HistoricidadRoleSelector: Historicidad roles', historicidadRoles);
      setAvailableRoles(historicidadRoles);

      // Si solo hay un rol, seleccionarlo automáticamente y guardarlo
      if (historicidadRoles.length === 1 && !selectedRole) {
        console.log('HistoricidadRoleSelector: Auto-selecting single role', historicidadRoles[0].value);
        const roleValue = historicidadRoles[0].value;
        localStorage.setItem(`historicidad_selected_role_${user.id}`, roleValue);
        onRoleChange(roleValue);
      }
    } catch (error) {
      console.error('HistoricidadRoleSelector: Error cargando roles disponibles:', error);
      setAvailableRoles([]);
    }
  };

  const getRoleDescription = (roleName) => {
    const descriptions = {
      'gestor': 'Gestiona datos en estado inicial',
      'validador': 'Valida información',
      'editor': 'Contrasta y aprueba información',
      'historiador': 'Crea borradores y publica directamente'
    };
    return descriptions[roleName] || '';
  };

  const handleRoleSelect = (roleValue) => {
    // Guardar la selección en localStorage para persistencia
    if (user && user.id) {
      localStorage.setItem(`historicidad_selected_role_${user.id}`, roleValue);
      console.log('HistoricidadRoleSelector: Saved role selection', roleValue);
    }
    onRoleChange(roleValue);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    if (user && user.id) {
      localStorage.removeItem(`historicidad_selected_role_${user.id}`);
      console.log('HistoricidadRoleSelector: Cleared role selection');
    }
    onRoleChange(null);
    setIsOpen(true);
  };

  // Si no hay roles disponibles, no mostrar nada
  if (availableRoles.length === 0) {
    return null;
  }

  // Si solo hay un rol, mostrar un badge simple (no selector)
  if (availableRoles.length === 1) {
    const role = availableRoles[0];
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <FiUser className="text-white text-lg" />
            </div>
          </div>
          <div className="flex-1">
            <div className="text-sm text-blue-600 font-medium">Operando como:</div>
            <div className="text-lg font-semibold text-blue-900">{role.label}</div>
            <div className="text-sm text-blue-700">{role.description}</div>
          </div>
        </div>
      </div>
    );
  }

  // Si hay múltiples roles, mostrar selector dropdown
  const currentRole = availableRoles.find(r => r.value === selectedRole);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {selectedRole ? 'Rol activo:' : 'Selecciona tu rol para operar:'}
          </label>
          {selectedRole && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Activo
            </span>
          )}
        </div>
        
        <div className="relative">
          <div className="flex gap-2">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`flex-1 flex items-center justify-between gap-3 p-4 border-2 rounded-lg transition-all ${
                selectedRole
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 flex-1 text-left">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedRole ? 'bg-blue-500' : 'bg-gray-300'
                }`}>
                  <FiUser className="text-white text-lg" />
                </div>
                <div className="flex-1">
                  {currentRole ? (
                    <>
                      <div className="text-lg font-semibold text-gray-900">
                        {currentRole.label}
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentRole.description}
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-500">
                      Selecciona un rol para continuar...
                    </div>
                  )}
                </div>
              </div>
              <FiChevronDown
                className={`flex-shrink-0 text-gray-400 transition-transform ${
                  isOpen ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            
            {selectedRole && availableRoles.length > 1 && (
              <button
                onClick={handleClearSelection}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all"
                title="Cambiar de rol"
              >
                <FiRefreshCw className="text-gray-600 hover:text-red-600" size={20} />
              </button>
            )}
          </div>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {availableRoles.map((role) => (
                <button
                  key={role.value}
                  onClick={() => handleRoleSelect(role.value)}
                  className={`w-full flex items-center gap-3 p-4 transition-colors text-left ${
                    selectedRole === role.value
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedRole === role.value ? 'bg-blue-500' : 'bg-gray-300'
                  }`}>
                    <FiUser className="text-white text-lg" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {role.label}
                      {selectedRole === role.value && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          Actual
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {role.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!selectedRole && availableRoles.length > 1 && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 font-bold">⚠️</span>
              <div className="text-sm text-yellow-800">
                <strong>Atención:</strong> Debes seleccionar un rol antes de poder operar en este módulo.
                Tienes {availableRoles.length} roles disponibles.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricidadRoleSelector;

